import { CommonErrorType } from '../../common/enums.js';
import { somethingWentWrongMsg, t } from '../../common/i18n/index.js';
import { isInBrowser } from '../../common/utils/ssrUtils.js';
import type { DialogParams } from '../../common/types/dialog.js';
import type { CommonError } from '../../common/types/errors.js';

export interface DialogData {
  errors?: CommonError[];
  params?: DialogParams;
  submitInProgress?: boolean;
  visible?: boolean;
}

export interface DialogStore {
  getDialog: () => DialogData;
  openDialog: (params: DialogParams) => void;
  closeDialog: () => void;
  setDialogSubmitting: (submitInProgress: boolean) => void;
  setDialogErrors: (errors: CommonError[] | undefined) => void;
  updateDialog: (patch: Partial<DialogData>) => void;
  subscribe: (listener: () => void) => () => void;
}

// Stable, frozen empty state for SSR snapshot
export const EMPTY_DIALOG: DialogData = Object.freeze({});

/**
 * Creates an isolated dialog store instance.
 * Use this in SSR to create a fresh store per request and avoid cross-request leakage.
 */
export const createDialogStore = (): DialogStore => {
  let state: DialogData = EMPTY_DIALOG;
  const listeners = new Set<() => void>();

  const emit = () => listeners.forEach(listener => listener());

  return {
    getDialog: () => state,
    openDialog: params => {
      state = { params, visible: true };
      emit();
    },
    closeDialog: () => {
      state = EMPTY_DIALOG;
      emit();
    },
    setDialogSubmitting: submitInProgress => {
      state = { ...state, submitInProgress, errors: submitInProgress ? undefined : state.errors };
      emit();
    },
    setDialogErrors: errors => {
      state = { ...state, errors, submitInProgress: false };
      emit();
    },
    // Used when a dialog's params (or other partial data) need to change without a full re-open, e.g. showing
    // an info dialog on top of the currently open one, or updating params in place.
    updateDialog: patch => {
      state = { ...state, ...patch };
      emit();
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};

// Default instance for client-side use (safe: one user per browser tab).
const defaultStore = createDialogStore();

/**
 * Guards the shared singleton against server-side mutation.
 * NOTE: this does NOT make dialogs work during SSR — it only makes the attempt visible instead of silent.
 */
const isBrowserOnlyCallAllowed = (fnName: string): boolean => {
  if (isInBrowser()) {
    return true;
  }
  // Deliberately not thrown, because this is a non-critical SSR issue (the dialog state is just ignored on the server).
  console.warn(
    `dialogStore.${fnName}() was called outside the browser. Dialog state is a process-wide singleton on the ` +
      `server and must not be used during SSR, so the call was ignored.`
  );
  return false;
};

// Non-React accessor for client-side non-React code.
export const getDialog = defaultStore.getDialog;

export const openDialog = (params: DialogParams): void => {
  if (isBrowserOnlyCallAllowed('openDialog')) {
    defaultStore.openDialog(params);
  }
};

export const closeDialog = (): void => {
  if (isBrowserOnlyCallAllowed('closeDialog')) {
    defaultStore.closeDialog();
  }
};

export const setDialogSubmitting = (submitInProgress: boolean): void => {
  if (isBrowserOnlyCallAllowed('setDialogSubmitting')) {
    defaultStore.setDialogSubmitting(submitInProgress);
  }
};

export const setDialogErrors = (errors: CommonError[] | undefined): void => {
  if (isBrowserOnlyCallAllowed('setDialogErrors')) {
    defaultStore.setDialogErrors(errors);
  }
};

// Convenience for the most common failure case
export const setDialogSubmitFailed = (): void => {
  setDialogErrors([{ type: CommonErrorType.SYSTEM, message: t.JDTO(somethingWentWrongMsg) }]);
};

export const updateDialog = (patch: Partial<DialogData>): void => {
  if (isBrowserOnlyCallAllowed('updateDialog')) {
    defaultStore.updateDialog(patch);
  }
};

// Subscription glue for useSyncExternalStore (React face, see DialogProvider.tsx)
export const subscribeDialog = defaultStore.subscribe;
